import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import BrandsFilter from 'components/brands-filter/BrandsFilter';
import Spinner from 'components/spinner/Spinner';

import InfluencerCampaignHistorySorting from './InfluencerCampaignHistorySorting';
import CampaignHistoryCard from 'modules/campaigns/components/campaign-history-card/CampaignHistoryCard';

import { t } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';
import { isInfluencerProfileRejected } from 'utils/profile';
import { isCcmModerator } from 'utils/account';

import type {
  BrandProfile,
  InfluencerCampaignHistory,
  InfluencerProfile,
  Organization,
  Role
} from 'types';

type OwnProps = {
  filter: string,
  influencerProfile: InfluencerProfile,
  campaignHistory: InfluencerCampaignHistory,
  campaignHistoryLoading: boolean,
  isFreemium: boolean | null | undefined,
  mapByOrganizationId: {
    [x: number]: Organization
  }
};

type StateProps = {
  roles: Array<Role>
};

type Props = OwnProps & StateProps;

type State = {
  sortingOption: string
};

class InfluencerCampaignHistoryTab extends React.Component<Props, State> {
  state = {
    sortingOption: 'newest'
  };

  handleSortingChange = (sortingOption: string) => {
    this.setState({
      sortingOption
    });
  };

  isMessengerDisabled(): boolean {
    const { influencerProfile } = this.props;

    return isInfluencerProfileRejected(influencerProfile);
  }

  isDisabled(brandProfile: BrandProfile): boolean {
    const { mapByOrganizationId } = this.props;

    if (!brandProfile || !brandProfile.organizationId) {
      return false;
    }

    return !mapByOrganizationId[brandProfile.organizationId];
  }

  renderSorting(): React.ReactNode {
    const { sortingOption } = this.state;

    return (
      <div className="influencer-campaign-history-tab__sorting">
        <InfluencerCampaignHistorySorting
          onChange={this.handleSortingChange}
          value={sortingOption}
        />
      </div>
    );
  }

  renderCampaignHistory(): React.ReactNode {
    const { campaignHistory, filter, roles } = this.props;
    const { sortingOption } = this.state;
    const isModerator = isCcmModerator(roles);
    const sortedCampaignHistory = [...campaignHistory].sort((a, b) => (
      sortingOption === 'newest'
        ? b.campaign.startDate.localeCompare(a.campaign.startDate)
        : a.campaign.startDate.localeCompare(b.campaign.startDate)
    ));

    const shouldShowEmptyMessage = !isModerator
      && sortedCampaignHistory
        .every(item => (
          item.campaign.campaignType === 'ccm'
        ));

    return (
      <React.Fragment>
        <div className="influencer-campaign-history-tab__heading">
          <span className="influencer-campaign-history-tab__title">
            {t('influencer_campaign_history.chronology_title')}
          </span>
          <div className="influencer-campaign-history-tab__filter">
            <BrandsFilter />
          </div>
          { this.renderSorting() }
        </div>
        <div>
          {
            !shouldShowEmptyMessage && (sortedCampaignHistory || []).map(item => {
              const hideCcmCard = item.campaign.campaignType === 'ccm' && !isModerator;

              return (
                <div
                  className="influencer-campaign-history-tab__history-card"
                  key={`influencer-campaign-history-card-${item.subcampaign.id}`}
                >
                  <CampaignHistoryCard
                    key={item.subcampaign.id}
                    item={item}
                    hideCcmCard={hideCcmCard}
                    events={item.negotiationHistory || []}
                    disabled={this.isDisabled(item.campaign.brandProfile)}
                    disabledMessenger={this.isMessengerDisabled()}
                  />
                </div>
              );
            })
          }
        </div>
        {
          (isEmpty(sortedCampaignHistory) || shouldShowEmptyMessage) && (
            <div
              className="influencer-campaign-history-tab__no-history"
              dangerouslySetInnerHTML={{
                __html: sanitizeGenericHtml(t(`influencer_campaign_history_summary.message_no_history_found_with_${filter}`))
              }}
            />
          )
        }
      </React.Fragment>
    );
  }

  render() {
    const { campaignHistoryLoading, isFreemium } = this.props;

    if (isFreemium) {
      return (
        <div className="influencer-campaign-history-tab__no_freemium_access">
          <div>
            {t('influencer_campaign_history.freemium_no_access')}
          </div>
          <div>
            <a
              href={t('influencer_campaign_history.freemium_no_access_email')}
              className="influencer-campaign-history-tab__no_freemium_access-link"
            >
              {t('influencer_campaign_history.freemium_no_access_contact_team')}
            </a>
          </div>
        </div>
      );
    }

    return (
      <section className="influencer-campaign-history-tab">
        {
          campaignHistoryLoading ?
            <div className="influencer-campaign-history-tab__spinner">
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            </div> :
            this.renderCampaignHistory()
        }
      </section>
    );
  }
}

const mapStateToProps = ({ account }): StateProps => ({
  roles: account.roles
});

export default connect(mapStateToProps)(InfluencerCampaignHistoryTab);
